import React, { useState } from 'react';
import styled from 'styled-components';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { colors } from '../../../shared/styles/colors';
import { Subtitle1, Field, Button } from '../../../shared/ui';
import { axiosInstance } from 'shared/api/axios-instance';

type ContactModalProps = {
  title?: string;
  onSubmit?: (email: string, message: string) => void;
  isLoading?: boolean;
  appealType?: string;
};

const ModalWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    background:#F6F9FC;
    padding:0 80px 0 80px
    
 
`

const Form = styled.form`
    width: 100%;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    gap: 37px;
    background:#F6F9FC
`

const FieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    
`

const ErrorMessage = styled.div`
    color: ${colors.errorState};
    font-size: 12px;
`;

const contactSchema = z.object({
  email: z.string()
    .min(1, 'Email обязателен')
    .email('Пожалуйста, введите корректный email'),
  message: z.string()
    .min(1, 'Сообщение обязательно')
    .min(10, 'Сообщение должно содержать минимум 10 символов'),
  type_of_appeal: z.string().optional()
});

type ContactFormData = z.infer<typeof contactSchema>;

export const ContactModal: React.FC<ContactModalProps> = ({
  title = 'Заповніть форму і ми зв\'яжемося з вами',
  onSubmit,
  isLoading = false,
  appealType = 'general'
}) => {
  const [serverError, setServerError] = useState('');

  const methods = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      email: '',
      message: '',
      type_of_appeal: appealType === 'authorized' 
        ? 'authorized_user_appeal' 
        : appealType === 'unauthorized' 
        ? 'unauthorized_user_appeal' 
        : 'general'
    },
    mode: 'onSubmit'
  });

  const { handleSubmit, formState, reset } = methods;

  const onSubmitForm = handleSubmit(async (data: ContactFormData) => {
    try {
      setServerError('');
      
      if (onSubmit) {
        await onSubmit(data.email, data.message);
      } else {
        const response = await axiosInstance.post('/api/feedback/', {
          email: data.email,
          message: data.message,
          type_of_appeal: data.type_of_appeal
        });
        
      }
      
      reset();
      
    } catch (error: any) {
      console.error('Submission error:', error);
      
      if (error.response) {
        const errorMessage = error.response.data?.message || 'Произошла ошибка сервера';
        setServerError(errorMessage);
      } else if (error.request) {
        setServerError('Ошибка соединения. Проверьте интернет-соединение.');
      } else {
        setServerError('Произошла ошибка при отправке формы');
      }
    }
  });

  return (
    <FormProvider {...methods}>
      <ModalWrapper>
        <Form onSubmit={onSubmitForm}>
          <Subtitle1 align="center" color={colors.accentPrimary}>{title}</Subtitle1>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <FieldWrapper>
              <Controller
                name="email"
                control={methods.control}
                render={({ field }) => (
                  <Field
                    {...field}
                    type="email"
                    placeholder="E-mail"
                  />
                )}
              />
              {formState.errors.email && formState.isSubmitted && (
                <ErrorMessage>{formState.errors.email.message}</ErrorMessage>
              )}
            </FieldWrapper>

            <FieldWrapper>
              <Controller
                name="message"
                control={methods.control}
                render={({ field }) => (
                  <Field
                    {...field}
                    type="textarea"
                    height="133px"
                    placeholder="Повідомлення"
                  />
                )}
              />
              {formState.errors.message && formState.isSubmitted && (
                <ErrorMessage>{formState.errors.message.message}</ErrorMessage>
              )}
            </FieldWrapper>

            {serverError && <ErrorMessage>{serverError}</ErrorMessage>}
          </div>

          <Button
            type="submit"
            disabled={isLoading || formState.isSubmitting}
          >
            {isLoading || formState.isSubmitting ? 'Отправка...' : 'Надіслати'}
          </Button>
        </Form>
      </ModalWrapper>
    </FormProvider>
  );
};